<div class="trd-page">
  <app-project-nav></app-project-nav>

  @if (loading()) {
    <div class="trd-state">
      <mat-progress-spinner mode="indeterminate" diameter="40"></mat-progress-spinner>
    </div>
  } @else if (loadError()) {
    <div class="trd-state trd-state--error">{{ 'testResults.detail.loadError' | translate }}</div>
  } @else {
    <div class="trd-header">
      <div class="trd-heading">
        <div class="trd-eyebrow">{{ 'testResults.detail.eyebrow' | translate }}</div>
        <div class="trd-title-row">
          <h1 class="trd-title">{{ 'testResults.detail.title' | translate }}</h1>
          <code class="trd-run-key">{{ runKey() }}</code>
          <span class="trd-art" [class.trd-art--auto]="automated()">
            <mat-icon aria-hidden="true">{{ automated() ? 'settings' : 'manage_accounts' }}</mat-icon>
            {{ (automated() ? 'testResults.detail.automated' : 'testResults.detail.manual') | translate }}
          </span>
        </div>
        <div class="trd-subtitle" data-testid="result-header-title">
          @if (multiCase()) {
            @if (runName()) {
              {{ runName() }} ·
            }
            {{ 'testResults.detail.caseCount' | translate: { count: caseCount() } }}
          } @else {
            {{ caseTitle() }}
            @if (caseKey()) {
              ·
              <code class="trd-case-key">{{ caseKey() }}</code>
            }
          }
        </div>
      </div>
      <a class="trd-back" [routerLink]="overviewLink" [queryParams]="overviewQueryParams">
        {{ 'testResults.detail.backToOverview' | translate }}
        <mat-icon aria-hidden="true">chevron_right</mat-icon>
      </a>
    </div>

    <div class="trd-card trd-summary">
      <div class="trd-summary-main">
        <div class="trd-result-circle trd-result-circle--{{ statusKey(outcome()) }}">
          <mat-icon aria-hidden="true">{{ outcomeIcon() }}</mat-icon>
        </div>
        <div>
          <span class="trd-pill trd-pill--{{ statusKey(outcome()) }}">
            <span class="trd-pill-dot"></span>
            {{ 'testResults.result.' + statusKey(outcome()) | translate }}
          </span>
          <div class="trd-passed">
            {{ 'testResults.detail.summaryPassed' | translate: { passed: passed(), total: total() } }}
          </div>
        </div>
        <span class="trd-flex"></span>
        <div class="trd-metrics">
          <div class="trd-metric">
            <div class="trd-metric-label">{{ 'testResults.detail.metric.environment' | translate }}</div>
            <div class="trd-metric-value">{{ environment() }}</div>
          </div>
          <div class="trd-metric">
            <div class="trd-metric-label">{{ 'testResults.detail.metric.duration' | translate }}</div>
            <div class="trd-metric-value">{{ runDuration() }}</div>
          </div>
          <div class="trd-metric">
            <div class="trd-metric-label">{{ 'testResults.detail.metric.failed' | translate }}</div>
            <div class="trd-metric-value">{{ failed() }}</div>
          </div>
          <div class="trd-metric">
            <div class="trd-metric-label">{{ 'testResults.detail.metric.skipped' | translate }}</div>
            <div class="trd-metric-value">{{ skipped() }}</div>
          </div>
        </div>
      </div>

      <div class="trd-meta">
        <span class="trd-source">
          <span class="trd-source-glyph trd-source-glyph--{{ systemKey() }}">
            {{ system().glyph }}
          </span>
          <strong>{{ system().labelKey | translate }}</strong>
        </span>
        @if (executedByName()) {
          <span class="trd-sep">·</span>
          <app-avatar [name]="executedByName()" [size]="22"></app-avatar>
          <span class="trd-exec">{{ executedByName() }}</span>
        }
        @if (completedAt()) {
          <span class="trd-sep">·</span>
          <span class="trd-when">{{ completedAt() | date: 'dd.MM.yyyy, HH:mm' }}</span>
        }
        @if (unmatchedTests(); as count) {
          <span class="trd-sep">·</span>
          <a class="trd-unmatched" data-testid="unmatched-tests-hint" [routerLink]="ciMappingLink">
            {{ unmatchedTestsKey() | translate: { count } }}
            <span aria-hidden="true">›</span>
          </a>
        }
        <span class="trd-flex"></span>
        <mat-icon class="trd-frozen-icon" aria-hidden="true">lock</mat-icon>
        <span class="trd-frozen">{{ 'testResults.detail.frozen' | translate }}</span>
      </div>

      <div class="trd-stats">
        <span class="trd-stats-label">{{ 'testResults.detail.stats.label' | translate }}</span>
        <span class="trd-stat trd-stat--total">
          {{ 'testResults.detail.stats.total' | translate }}
          <strong>{{ total() }}</strong>
        </span>
        <span class="trd-stat trd-stat--pass">
          {{ 'testResults.detail.stats.pass' | translate }}
          <strong>{{ passed() }}</strong>
        </span>
        <span class="trd-stat trd-stat--fail">
          {{ 'testResults.detail.stats.fail' | translate }}
          <strong>{{ failed() }}</strong>
        </span>
        <span class="trd-stat trd-stat--blocked">
          {{ 'testResults.detail.stats.blocked' | translate }}
          <strong>{{ blocked() }}</strong>
        </span>
        <span class="trd-stat trd-stat--skip">
          {{ 'testResults.detail.stats.skip' | translate }}
          <strong>{{ skipped() }}</strong>
        </span>
      </div>
    </div>

    <h2 class="trd-steps-heading">{{ 'testResults.detail.stepsHeading' | translate }}</h2>
    <div class="trd-card">
      @if (multiCase()) {
        @for (group of caseGroups(); track group.id) {
          <section class="trd-case" data-testid="result-case-group">
            <div class="trd-case-head">
              @if (group.key) {
                <code class="trd-case-head-key">{{ group.key }}</code>
              }
              <span class="trd-case-head-title">{{ group.title }}</span>
              <span class="trd-pill trd-pill--{{ statusKey(group.status) }} trd-case-pill" data-testid="case-status">
                <span class="trd-pill-dot"></span>
                {{ 'testResults.result.' + statusKey(group.status) | translate }}
              </span>
            </div>
            <ng-container [ngTemplateOutlet]="stepsHead" />
            @if (group.steps.length === 0) {
              <div class="trd-empty">{{ 'testResults.detail.caseEmpty' | translate }}</div>
            }
            <ng-container [ngTemplateOutlet]="stepRows" [ngTemplateOutletContext]="{ $implicit: group.steps }" />
          </section>
        }
      } @else {
        <ng-container [ngTemplateOutlet]="stepsHead" />
        @if (steps().length === 0) {
          <div class="trd-empty">{{ 'testResults.detail.empty' | translate }}</div>
        }
        <ng-container [ngTemplateOutlet]="stepRows" [ngTemplateOutletContext]="{ $implicit: steps() }" />
      }
    </div>
  }

  <ng-template #stepsHead>
    <div class="trd-steps-head">
      <div class="trd-col-nr">{{ 'testResults.detail.table.number' | translate }}</div>
      <div class="trd-col-step">{{ 'testResults.detail.table.step' | translate }}</div>
      <div class="trd-col-duration">{{ 'testResults.detail.table.duration' | translate }}</div>
      <div class="trd-col-result">{{ 'testResults.detail.table.result' | translate }}</div>
    </div>
  </ng-template>

  <ng-template #stepRows appStepRows let-rows>
    @for (step of rows; track step.result.id) {
      @if (step.showGroupHeader) {
        <div class="trd-group">
          <mat-icon aria-hidden="true">content_copy</mat-icon>
          <span>{{ step.group }}</span>
          @if (step.groupIteration !== null) {
            <span data-testid="group-iteration">
              {{ 'testResults.detail.iteration' | translate: { n: step.groupIteration } }}
            </span>
          }
          @if (step.groupStatus) {
            <span
              class="trd-pill trd-pill--{{ statusKey(step.groupStatus) }} trd-group-pill"
              data-testid="group-status"
            >
              <span class="trd-pill-dot"></span>
              {{ 'testResults.result.' + statusKey(step.groupStatus) | translate }}
            </span>
          }
        </div>
      }
      <div class="trd-step">
        <div class="trd-col-nr" [class.trd-col-nr--indent]="step.indented">{{ step.number }}</div>
        <div class="trd-col-step">
          <div class="trd-step-text">{{ step.result.descriptionSnapshot }}</div>
          @if (step.result.expectedSnapshot) {
            <div class="trd-soll">
              <strong>{{ 'testResults.detail.soll' | translate }}</strong>
              {{ step.result.expectedSnapshot }}
            </div>
          }
          @if (step.result.actualResult) {
            <div class="trd-ist" [class.trd-ist--fail]="step.result.status === 'FAILED'">
              <strong>{{ 'testResults.detail.ist' | translate }}</strong>
              {{ step.result.actualResult }}
            </div>
          }
          @if (step.result.comment) {
            <div class="trd-comment">{{ step.result.comment }}</div>
          }
          @if (evidenceFor(step.result.id).length > 0) {
            <div class="trd-evidence">
              @for (item of evidenceFor(step.result.id); track item.id) {
                <img
                  class="trd-evidence-thumb"
                  [src]="item.url"
                  [alt]="item.filename"
                  role="button"
                  tabindex="0"
                  [attr.aria-label]="'testResults.detail.evidenceView' | translate"
                  (click)="openLightbox(item.url)"
                  (keydown.enter)="openLightbox(item.url)"
                />
              }
            </div>
          }
          @if (step.result.status === 'FAILED') {
            <div class="trd-bug">
              @if (linkedBugKey(step.result); as key) {
                <a
                  class="trd-bug__linked"
                  role="button"
                  tabindex="0"
                  [attr.aria-label]="'testResults.detail.bug.jumpAria' | translate: { key: key }"
                  (click)="openLinkedBug(step.result)"
                  (keydown.enter)="openLinkedBug(step.result)"
                >
                  🐞 {{ 'testResults.detail.bug.linked' | translate: { key: key } }}
                </a>
              } @else {
                <button
                  type="button"
                  class="trd-bug__create"
                  [disabled]="isCreatingBug(step.result)"
                  (click)="createBug(step.result)"
                >
                  @if (isCreatingBug(step.result)) {
                    <mat-progress-spinner diameter="14" mode="indeterminate"></mat-progress-spinner>
                  } @else {
                    <mat-icon aria-hidden="true">add</mat-icon>
                  }
                  {{ 'testResults.detail.bug.create' | translate }}
                </button>
              }
            </div>
            @if (bugErrorFor(step.result); as failure) {
              <div class="trd-bug__error" role="alert">
                {{ failure.key | translate: failure.params }}
                @if (failure.accountLink) {
                  <a routerLink="/account">{{ toAccountKey | translate }}</a>
                }
              </div>
            }
          }
          @if ((step.result.defects?.length ?? 0) > 0 || canCaptureDefect(step.result)) {
            <div class="trd-defects">
              @if (step.result.defects?.length) {
                <ul class="trd-defects__keys" [attr.aria-label]="'defect.capture.keysLabel' | translate">
                  @for (defect of step.result.defects; track defect.id) {
                    <li data-testid="defect-key"><app-code-id-chip [id]="defect.key" /></li>
                  }
                </ul>
              }
              @if (canCaptureDefect(step.result)) {
                <button
                  type="button"
                  class="trd-defects__capture"
                  data-testid="defect-erfassen"
                  [attr.aria-label]="'defect.capture.buttonAria' | translate: { n: step.number }"
                  (click)="captureDefect(step.result)"
                >
                  <mat-icon aria-hidden="true">bug_report</mat-icon>
                  {{ 'defect.capture.button' | translate }}
                </button>
              }
            </div>
          }
        </div>
        <div class="trd-col-duration" [title]="stepDuration(step.result)">{{ stepDuration(step.result) }}</div>
        <div class="trd-col-result">
          <span class="trd-pill trd-pill--{{ statusKey(step.result.status) }}">
            <span class="trd-pill-dot"></span>
            {{ 'testResults.result.' + statusKey(step.result.status) | translate }}
          </span>
        </div>
      </div>
    }
  </ng-template>

  @if (lightboxUrl(); as url) {
    <div
      class="trd-lightbox"
      role="button"
      tabindex="0"
      [attr.aria-label]="'testResults.detail.lightboxClose' | translate"
      (click)="closeLightbox()"
      (keydown.enter)="closeLightbox()"
      (keydown.escape)="closeLightbox()"
    >
      <img class="trd-lightbox-img" [src]="url" [alt]="'testResults.detail.lightboxAlt' | translate" />
    </div>
  }
</div>

<app-defect-erfassen-dialog
  [projectId]="projectId"
  [stepResultId]="defectStepId()"
  (created)="onDefectCreated($event)"
  (closed)="defectStepId.set(null)"
/>
